import { css } from '@emotion/react';
import { useMemo } from 'react';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import { sizes, } from './constants';
const buttonHeights = {
    [sizes.large]: 48,
    [sizes.medium]: 40,
    [sizes.small]: 24,
};
const butttonBorderWidth = 1;
const selectedButtonBorderWidth = 2;
const horizontalPadding = theme.spacing.x3;
const textSizes = {
    large: theme.typeStyles.captionLargeStyles,
    medium: theme.typeStyles.captionMediumStyles,
    small: theme.typeStyles.captionMediumStyles,
};
const verticalPaddings = {
    large: styleUtils.rem(styleUtils.remToPxValue(theme.spacing.x4) - (styleUtils.remToPxValue(theme.spacing.base) / 2)),
    medium: theme.spacing.x3,
    small: theme.spacing.x2,
};
const borderWidthDifference = selectedButtonBorderWidth - butttonBorderWidth;
const selectedButtonHorizontalPadding = styleUtils.remToPxValue(horizontalPadding) - borderWidthDifference;
const selectedButtonVerticalPaddings = {
    large: styleUtils.remToPxValue(verticalPaddings.large) - borderWidthDifference,
    medium: styleUtils.remToPxValue(verticalPaddings.medium) - borderWidthDifference,
    small: styleUtils.remToPxValue(verticalPaddings.small) - borderWidthDifference,
};
const alternativeColors = {
    destructiveLight: '#eba9b6',
    destructiveStrong: '#a21e39',
    facebook: '#1877f2',
    facebookDark: '#29487d',
    facebookLight: '#b3c2e1',
};
export const variationStyles = {
    destructive: {
        activeBackgroundColor: alternativeColors.destructiveStrong,
        backgroundColor: theme.colors.negative800,
        borderColor: undefined,
        disabledBackgroundColor: alternativeColors.destructiveLight,
        disabledBorderColor: undefined,
        disabledTextColor: undefined,
        hoverBackgroundColor: alternativeColors.destructiveStrong,
        hoverBorderColor: undefined,
        hoverTextColor: theme.colors.gray100,
        textColor: theme.colors.gray100,
    },
    destructiveSecondary: {
        activeBackgroundColor: undefined,
        backgroundColor: theme.colors.gray100,
        borderColor: alternativeColors.destructiveStrong,
        disabledBackgroundColor: theme.colors.gray100,
        disabledBorderColor: alternativeColors.destructiveLight,
        disabledTextColor: alternativeColors.destructiveLight,
        hoverBackgroundColor: undefined,
        hoverBorderColor: alternativeColors.destructiveStrong,
        hoverTextColor: theme.colors.negative800,
        textColor: theme.colors.negative800,
    },
    facebook: {
        activeBackgroundColor: undefined,
        backgroundColor: alternativeColors.facebook,
        borderColor: undefined,
        disabledBackgroundColor: alternativeColors.facebookLight,
        disabledBorderColor: undefined,
        disabledTextColor: undefined,
        hoverBackgroundColor: alternativeColors.facebookDark,
        hoverBorderColor: undefined,
        hoverTextColor: theme.colors.gray100,
        textColor: theme.colors.gray100,
    },
    primary: {
        activeBackgroundColor: theme.colors.accent700,
        backgroundColor: theme.colors.accent700,
        borderColor: undefined,
        disabledBackgroundColor: theme.colors.accent400,
        disabledBorderColor: undefined,
        disabledTextColor: undefined,
        hoverBackgroundColor: theme.colors.accent800,
        hoverBorderColor: undefined,
        hoverTextColor: theme.colors.gray100,
        textColor: theme.colors.accent100,
    },
    secondary: {
        activeBackgroundColor: undefined,
        backgroundColor: theme.colors.accent100,
        borderColor: theme.colors.accent700,
        disabledBackgroundColor: undefined,
        disabledBorderColor: theme.colors.accent500,
        disabledTextColor: theme.colors.accent500,
        hoverBackgroundColor: theme.colors.accent100,
        hoverBorderColor: theme.colors.accent700,
        hoverTextColor: theme.colors.accent700,
        textColor: theme.colors.accent700,
    },
    tertiary: {
        activeBackgroundColor: theme.colors.accent300,
        backgroundColor: 'transparent',
        borderColor: undefined,
        disabledBackgroundColor: 'transparent',
        disabledBorderColor: undefined,
        disabledTextColor: theme.colors.accent500,
        hoverBackgroundColor: theme.colors.accent300,
        hoverBorderColor: undefined,
        hoverTextColor: theme.colors.accent800,
        textColor: theme.colors.accent800,
    },
};
const useButtonStyles = ({ expand = false, expandAt = undefined, iconAlignment = undefined, size, type, }) => (useMemo(() => (css `
        ${styleUtils.buttonReset}
        ${styleUtils.borderBox}
        ${textSizes[size]}
        ${type === 'primary' && styleUtils.transition([
    'color',
    'background-color',
])}
        align-items: center;
        background-color: ${variationStyles[type].backgroundColor};
        border: none;
        color: ${variationStyles[type].textColor};
        display: inline-flex;
        height: ${styleUtils.rem(buttonHeights[size])};
        justify-content: center;
        letter-spacing: ${styleUtils.rem(0.5)};
        overflow: hidden;
        padding-bottom: ${verticalPaddings[size]};
        padding-left: ${horizontalPadding};
        padding-right: ${horizontalPadding};
        padding-top: ${verticalPaddings[size]};
        text-decoration: none;
        vertical-align: bottom;
        white-space: nowrap;
        ${expand && 'width: 100%;'}
        ${expandAt && css `
          ${theme.media.lessThan(expandAt)(css `
            width: 100%;
          `)}
        `}
        ${iconAlignment && iconAlignment === 'right' && css `
          flex-direction: row-reverse;
        `}
        ${variationStyles[type].borderColor && css `
          border-color: ${variationStyles[type].borderColor};
          border-style: solid;
          border-width: ${styleUtils.rem(butttonBorderWidth)};
        `}

        &:focus,
        &:hover {
          color: ${variationStyles[type].hoverTextColor};
          cursor: pointer;
          text-decoration: none;
        }

        ${variationStyles[type].activeBackgroundColor && css `
          &:active {
            background-color: ${variationStyles[type].activeBackgroundColor};
          }
        `}

        &:focus,
        &:active,
        &:hover {
          background-color: ${variationStyles[type].hoverBackgroundColor};
          ${variationStyles[type].hoverBorderColor && css `
            border-color: ${variationStyles[type].hoverBorderColor};
            border-width: ${styleUtils.rem(selectedButtonBorderWidth)};
            padding-bottom: ${styleUtils.rem(selectedButtonVerticalPaddings[size])};
            padding-left: ${styleUtils.rem(selectedButtonHorizontalPadding)};
            padding-right: ${styleUtils.rem(selectedButtonHorizontalPadding)};
            padding-top: ${styleUtils.rem(selectedButtonVerticalPaddings[size])};
          `}
          color: ${variationStyles[type].hoverTextColor};
        }

        &:disabled {
          cursor: default;
          color: ${variationStyles[type].disabledTextColor};
          ${variationStyles[type].disabledBackgroundColor && css `
            background-color: ${variationStyles[type].disabledBackgroundColor};
          `}
          ${variationStyles[type].disabledBorderColor && css `
            border-color: ${variationStyles[type].disabledBorderColor};
          `}
        }

        & > span {
          overflow: hidden;
          text-overflow: ellipsis;
        }
      `), [
    expand,
    expandAt,
    iconAlignment,
    size,
    type,
]));
export default useButtonStyles;
